import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  MapearErrosServidor,
  MapearErrosServidorCampo,
} from "src/hook/Functions";
import {
  TributacaoCfopImpostoCadastroDto,
  TributacaoCfopImpostoCadastroSchema,
} from "./TributacaoCfopImpostoCadastro.Dto";
import {
  TributacaoCfopImpostoAlterar,
  TributacaoCfopImpostoBuscarId,
  TributacaoCfopImpostoIncluir,
} from "src/services/Fiscal.Service";
import { showToast } from "src/componentes";
import TributacaoCfopImpostoCadastroMenu from "./TributacaoCfopImpostoCadastro.Menu";

type cst = {
  value?: string;
  label: string;
};

const TributacaoCfopImpostoCadastroCode = () => {
  const [loading, setLoading] = useState(true);
  const [incluir, setIncluir] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");
  const tributacaoCfopId = searchParams.get("tributacaocfopid");
  const type = searchParams.get("type");
  const [selectedCSTValue, setSelectedCSTValue] = useState<string>("");
  const menu = TributacaoCfopImpostoCadastroMenu({ id, setLoading });

  const form = useForm<TributacaoCfopImpostoCadastroDto>({
    resolver: yupResolver(TributacaoCfopImpostoCadastroSchema),
  });

  useEffect(() => {
    const controller = new AbortController();
    const Load = async () => {
      try {
        if (id) {
          setLoading(true);
          const response = await TributacaoCfopImpostoBuscarId(id);
          if (response.data.success) {
            const data = response.data.data as TributacaoCfopImpostoCadastroDto;
            data.aliquotaSimples = data.aliquotaSimples
              .toString()
              .replace(".", ",");
            form.reset(data);
            setIncluir(false);
            setSelectedCSTValue(String(data.cst));
          } else {
            showToast({
              message: "Ocorreu erro ao carregar TributacaoCfopImposto",
              title: "Erro",
              variant: "Danger",
            });
          }
        } else {
          setIncluir(true);
        }
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    };
    Load();
    return () => {
      controller.abort();
    };
  }, [id]);

  const getCstOptions = () => {
    if (type === "0") {
      //ICMS
      return [
        { label: "Selecione" },
        {
          value: "101",
          label: "101-Tributação ICMS pelo Simples Nacional",
        },
        {
          value: "102",
          label: "102-Tributada pelo Simples Nacional sem permissão de crédito",
        },
        {
          value: "103",
          label:
            "103-Isenção do ICMS no Simples Nacional para faixa de receita bruta",
        },
        { value: "300", label: "300-Imune" },
        {
          value: "400",
          label: "400-Não tributada pelo Simples Nacional",
        },
        {
          value: "500",
          label: "500-ICMS cobrado anteriormente por substituição tributária",
        },
      ];
    } else if (type === "1") {
      //IPI
      return [
        { label: "Selecione" },
        { value: "0", label: "00 - Entrada com recuperação de crédito" },
        { value: "1", label: "01 - Entrada tributada com alíquota zero" },
        { value: "2", label: "02 - Entrada isenta" },
        { value: "3", label: "03 - Entrada não-tributada" },
        { value: "4", label: "04 - Entrada imune" },
        { value: "5", label: "05 - Entrada com suspensão" },
        { value: "49", label: "49 - Outras entradas" },
        { value: "50", label: "50 - Saída tributada" },
        { value: "51", label: "51 - Saída tributada com alíquota zero" },
        { value: "52", label: "52 - Saída isenta" },
        { value: "53", label: "53 - Saída não-tributada" },
        { value: "54", label: "54 - Saída imune" },
        { value: "55", label: "55 - Saída com suspensão" },
        { value: "99", label: "99 - Outras saídas" },
      ];
    } else if (type === "2" || type === "3") {
      //PIS
      return [
        { label: "Selecione" },
        { value: "1", label: "01 - Operação Tributável com Alíquota Básica" },
        {
          value: "2",
          label: "02 - Operação Tributável com Alíquota Diferenciada",
        },
        {
          value: "3",
          label:
            "03 - Operação Tributável com Alíquota por Unidade de Medida de Produto",
        },
        {
          value: "4",
          label:
            "04 - Operação Tributável Monofásica - Revenda a Alíquota Zero",
        },
        {
          value: "5",
          label: "05 - Operação Tributável por Substituição Tributária",
        },
        { value: "6", label: "06 - Operação Tributável a Alíquota Zero" },
        { value: "7", label: "07 - Operação Isenta da Contribuição" },
        { value: "8", label: "08 - Operação sem Incidência da Contribuição" },
        { value: "9", label: "09 - Operação com Suspensão da Contribuição" },
        { value: "99", label: "99 - Outras Operações" },
      ];
    } else {
      return [{ label: "Selecione" }];
    }
  };

  const onSubmit = async (data: TributacaoCfopImpostoCadastroDto, e: any) => {
    e.preventDefault();
    setLoading(true);
    data.tributacaoCFOPId = Number(tributacaoCfopId);
    data.aliquotaSimples = data.aliquotaSimples?.toString().replace(",", ".");
    data.tipoImposto = Number(type);
    data.cst = Number(data.cst);

    if (incluir === true) {
      TributacaoCfopImpostoIncluir(data)
        .then((response) => {
          if (response.data.success) {
            navigate(`/fiscal/tributacao/cfop/cadastro?id=${tributacaoCfopId}`);
          } else {
            MapearErrosServidorCampo(
              response.data.notifications,
              form.setError
            );
            MapearErrosServidor(response.data.notifications, showToast);
          }
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      data.id = Number(id);
      TributacaoCfopImpostoAlterar(data)
        .then((response) => {
          if (response.data.success) {
            navigate(`/fiscal/tributacao/cfop/cadastro?id=${tributacaoCfopId}`);
          } else {
            MapearErrosServidorCampo(
              response.data.notifications,
              form.setError
            );
            MapearErrosServidor(response.data.notifications, showToast);
          }
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  return {
    loading,
    onSubmit,
    form,
    getCstOptions,
    selectedCSTValue,
    setSelectedCSTValue,
    menu,
  };
};
export default TributacaoCfopImpostoCadastroCode;
